import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import NewLearningHubPost from './new-learning-hub-post'
import LearningHubPostsFilterFields from '../components/learning-hub-posts-filter-fields'
import { LEARNING_HUB_DISCUSSION_TYPE_OPTIONS } from '../../constants'
import { Modal } from 'components'
import { Button, Spinner } from 'lp-components'
import { Discussions } from 'educator-portal-components'
import {
  buildSearchQueryString,
  parseSearchQueryObject,
  useCommunity,
  setDiscussionsSortType,
  getBookmarkedIdsByType,
  setCommunityOfPracticeId,
  hasLoadedAssociationForCommunityOfPractice,
} from 'utils'
import {
  DISCUSSIONS_PAGE_SIZE,
  LEARNING_HUB_ROUTE,
  NON_AFFILIATED_COP_ID,
} from 'config'
import { get, isEmpty, size } from 'lodash'
import * as routerActions from 'react-router-redux'
import * as apiActions from 'api-actions'
import * as flashActions from 'redux-flash'
import { selectors as apiSelectors } from 'lp-redux-api'
import { selectors as lhSelectors } from 'learning-hub-reducer'
import * as LS from 'local-storage'

const propTypes = {
  posts: PropTypes.arrayOf(Types.post),
  postsCount: PropTypes.number.isRequired,
  fetchDiscussions: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  hasLoadFailed: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  push: PropTypes.func.isRequired,
  postsPageCount: PropTypes.number.isRequired,
  addBookmark: PropTypes.func.isRequired,
  removeBookmark: PropTypes.func.isRequired,
  addLike: PropTypes.func.isRequired,
  destroyLike: PropTypes.func.isRequired,
  bookmarks: PropTypes.arrayOf(Types.bookmark).isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  searchOptions: Types.postsFilterOptions,
  fetchSearchOptions: PropTypes.func.isRequired,
  district: Types.district,
  isSchoolResolved: PropTypes.bool,
  userDetails: Types.user,
  learningHubCatalog: PropTypes.object,
  fetchLearningHubCourses: PropTypes.func.isRequired,
}

const defaultProps = {
  posts: null,
  searchOptions: null,
  district: null,
  isSchoolResolved: false,
  learningHubCatalog: undefined,
}

function Forum({
  posts,
  postsCount,
  fetchDiscussions,
  isLoading,
  hasLoadFailed,
  location: { query: queryHash },
  push,
  postsPageCount,
  addBookmark,
  removeBookmark,
  addLike,
  destroyLike,
  bookmarks,
  flashSuccessMessage,
  flashErrorMessage,
  searchOptions,
  fetchSearchOptions,
  district,
  isSchoolResolved,
  userDetails,
  learningHubCatalog,
  fetchLearningHubCourses,
}) {
  const community = useCommunity()
  const blockList = useMemo(
    () => get(community, 'search.forumFilters.blockList', []),
    [community]
  )
  const hasCorrectPosts = hasLoadedAssociationForCommunityOfPractice(
    posts,
    NON_AFFILIATED_COP_ID
  )
  const [isPostFormOpen, setIsPostFormOpen] = useState(false)
  const [showSpinner, setShowSpinner] = useState(!hasCorrectPosts)
  const postRootPath = `${LEARNING_HUB_ROUTE}/forum/discussions`

  const {
    query = '',
    filters = {},
    page = 1,
    sortType = Types.LAST_ACTIVITY_SORT_TYPE,
  } = useMemo(() => parseSearchQueryObject(queryHash), [queryHash])
  const postsCountText = useMemo(() => {
    if (isLoading && showSpinner) return '-'
    if (postsCount === 1 || postsCount <= DISCUSSIONS_PAGE_SIZE)
      return postsCount
    return `${size(posts)} of ${postsCount}`
  }, [posts, postsCount, isLoading, showSpinner])

  const searchPosts = useCallback(
    (params, { updateUrl = true } = {}) => {
      setShowSpinner(updateUrl || !hasCorrectPosts)
      const paramsWithSort = setDiscussionsSortType(params)
      if (updateUrl) {
        push({
          pathname: `${LEARNING_HUB_ROUTE}/forum`,
          search: buildSearchQueryString(paramsWithSort),
        })
      }
      const paramsWithCoPId = setCommunityOfPracticeId(
        paramsWithSort,
        NON_AFFILIATED_COP_ID
      )
      return fetchDiscussions(
        paramsWithCoPId,
        getBookmarkedIdsByType(
          bookmarks,
          Types.BOOKMARKABLE.LEARNING_HUB_DISCUSSION
        )
      ).then(() => setShowSpinner(false))
    },
    [fetchDiscussions, hasCorrectPosts, bookmarks, push]
  )

  const hasAppliedFilters = !isEmpty(query) || !isEmpty(filters)

  useEffect(() => {
    searchPosts({ query, filters, page, sortType }, { updateUrl: false })
  }, [])

  const requestAddBookmark = useCallback((postId) => {
    addBookmark({
      bookmarkableId: postId,
      bookmarkableType: Types.BOOKMARKABLE.LEARNING_HUB_DISCUSSION,
    })
      .then(({ successMessage }) => {
        flashSuccessMessage(successMessage)
      })
      .catch(({ errorMessage }) => {
        flashErrorMessage(errorMessage)
      })
  }, [])
  const requestRemoveBookmark = useCallback((bookmarkId) => {
    removeBookmark({
      bookmarkId,
      bookmarkableType: Types.BOOKMARKABLE.LEARNING_HUB_DISCUSSION,
    })
      .then(({ successMessage }) => {
        flashSuccessMessage(successMessage)
      })
      .catch(({ errorMessage }) => {
        flashErrorMessage(errorMessage)
      })
  }, [])

  const requestAddLike = useCallback((likeableId) => {
    addLike({
      likeableId,
      likeableType: Types.LIKEABLE.LEARNING_HUB_DISCUSSION,
    }).catch(({ errorMessage }) => {
      flashErrorMessage(errorMessage)
    })
  }, [])

  const requestDestroyLike = useCallback((id) => {
    destroyLike({ id }).catch(({ errorMessage }) => {
      flashErrorMessage(errorMessage)
    })
  }, [])

  // Wait until the school request has resolved (or this is an artist session,
  // which has no school) before fetching forum search options. Previously the
  // options were only fetched when a district was present, so admins — and any
  // user without a district — got stuck on a perpetual spinner. A null
  // districtId is a valid query for /search_options.
  const isArtistSession = !!LS.getArtistAuthToken() && !LS.getSchoolAuthToken()
  const canFetchSearchOptions = isArtistSession || isSchoolResolved

  useEffect(() => {
    if (searchOptions || !canFetchSearchOptions) return
    fetchSearchOptions(
      { blockList },
      Types.SEARCH_OPTIONS_TYPE.FORUM,
      district?.id ?? null
    )
  }, [
    searchOptions,
    canFetchSearchOptions,
    blockList,
    district,
    fetchSearchOptions,
  ])

  useEffect(() => {
    if (learningHubCatalog === undefined) fetchLearningHubCourses()
  }, [learningHubCatalog, fetchLearningHubCourses])

  const learningHubCourseFilterOptions = useMemo(() => {
    const list = (learningHubCatalog?.disciplineGroups || []).flatMap(
      (g) => g.courses || []
    )
    return list.map((c) => ({
      key: c.title || c.name || `Course ${c.id}`,
      value: c.id,
    }))
  }, [learningHubCatalog])

  const mergedSearchOptions = useMemo(() => {
    if (!searchOptions) return null
    return {
      ...searchOptions,
      discussionTypes: LEARNING_HUB_DISCUSSION_TYPE_OPTIONS,
      learningHubCourses: learningHubCourseFilterOptions,
    }
  }, [searchOptions, learningHubCourseFilterOptions])

  if (!searchOptions || !mergedSearchOptions) return <Spinner />

  return (
    <div>
      <div className="heading-container">
        <div className="welcome-message">
          <h2>
            <strong>What's on your mind?</strong>
          </h2>
        </div>
        <div className="new-post-cta">
          <Button onClick={() => setIsPostFormOpen(true)}>
            Start New Discussion
          </Button>
        </div>
      </div>
      <Discussions
        posts={posts}
        numPages={postsPageCount}
        resultsPage={page}
        searchOptions={mergedSearchOptions}
        query={query}
        filters={filters}
        searchPosts={searchPosts}
        showSpinner={showSpinner}
        postsCount={postsCount}
        postsCountText={postsCountText}
        sortType={sortType}
        openPostForm={() => setIsPostFormOpen(true)}
        hasLoadFailed={hasLoadFailed}
        isFiltered={hasAppliedFilters}
        bookmarks={bookmarks}
        addBookmark={requestAddBookmark}
        removeBookmark={requestRemoveBookmark}
        addLike={requestAddLike}
        destroyLike={requestDestroyLike}
        postRootPath={postRootPath}
        canPost={true}
        userDetails={userDetails}
        bookmarkableType={Types.BOOKMARKABLE.LEARNING_HUB_DISCUSSION}
        postLikeableType={Types.LIKEABLE.LEARNING_HUB_DISCUSSION}
        filterFieldsComponent={LearningHubPostsFilterFields}
        filterToolbarVariant="learningHubPills"
      />
      {isPostFormOpen && (
        <Modal onClose={() => setIsPostFormOpen(false)}>
          <NewLearningHubPost
            onSubmitSuccess={({ id }) => {
              push(`${postRootPath}/${id}`)
              flashSuccessMessage('New discussion created!')
            }}
            onCancel={() => setIsPostFormOpen(false)}
          />
        </Modal>
      )}
    </div>
  )
}

Forum.propTypes = propTypes

Forum.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    isLoading: apiSelectors.isLoading(
      state,
      apiActions.fetchLearningHubDiscussions
    ),
    hasLoadFailed: apiSelectors.isFailure(
      state,
      apiActions.fetchLearningHubDiscussions
    ),
    posts: lhSelectors.posts(state),
    postsCount: lhSelectors.postsCount(state),
    postsPageCount: lhSelectors.postsPageCount(state),
    bookmarks: lhSelectors.bookmarks(state),
    searchOptions: lhSelectors.searchOptions(state),
    district: lhSelectors.school(state)?.district ?? null,
    isSchoolResolved:
      apiSelectors.isSuccess(state, apiActions.fetchSchoolAuthorized) ||
      apiSelectors.isFailure(state, apiActions.fetchSchoolAuthorized),
    userDetails: lhSelectors.userDetails(state),
    learningHubCatalog: lhSelectors.learningHubCatalog(state),
  }
}

const mapDispatchToProps = {
  fetchDiscussions: apiActions.fetchLearningHubDiscussions,
  addBookmark: apiActions.addLearningHubBookmark,
  removeBookmark: apiActions.removeLearningHubBookmark,
  addLike: apiActions.addLearningHubLike,
  destroyLike: apiActions.destroyLearningHubLike,
  fetchSearchOptions: apiActions.fetchSearchOptions,
  fetchLearningHubCourses: apiActions.fetchLearningHubCourses,
  push: routerActions.push,
  flashErrorMessage: flashActions.flashErrorMessage,
  flashSuccessMessage: flashActions.flashSuccessMessage,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(Forum)
